import { trackScreenViewed } from '@/lib/analytics/scribe/ui';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { DictationSettingsAnalyticsPorts } from '@heidi/dictate';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';

/**
 * Single analytics surface for the Dictation settings card, wherever it
 * renders — `/settings/defaults#dictation` on the legacy IA, the standalone
 * `/settings/dictate` page once the workspace-settings IA is on.
 *
 * Each method dispatches `trackEventV2` directly without
 * `options: { amplitude: false }` so events route to Amplitude +
 * Snowplow + Datadog. Dictate's existing dashboards live in Amplitude,
 * so we deliberately bypass the `trackButtonClicked` helper (which
 * hard-forces Amplitude off for the org-wide migration off it).
 */
export const dictationSettingsAnalyticsPorts: DictationSettingsAnalyticsPorts = {
  trackPageView: () => {
    trackScreenViewed({
      screen_name: ScreenName.DictationSettings,
    });
  },

  trackShortcutToggled: (isEnabled) => {
    track({
      event_name: TrackingEventName.ToggleUpdated,
      props: {
        toggle_type: 'dictation_shortcut_enabled',
        is_enabled: isEnabled,
        feature: 'dictation',
      },
    });
  },

  trackPlaybackToggled: (isEnabled) => {
    track({
      event_name: TrackingEventName.ToggleUpdated,
      props: {
        toggle_type: 'dictation_playback',
        is_enabled: isEnabled,
        feature: 'dictation',
      },
    });
  },

  trackSoundToggled: (isEnabled) => {
    track({
      event_name: TrackingEventName.ToggleUpdated,
      props: {
        toggle_type: 'dictation_sound_enabled',
        is_enabled: isEnabled,
        feature: 'dictation',
      },
    });
  },

  /**
   * The silent-capture warning's chime (desktop-only, device-local
   * preference). The warning itself is never muted, so this measures how many
   * clinicians want the audible half off — the question the once-per-recording
   * cap did not answer.
   */
  trackCaptureSilenceChimeToggled: (isEnabled) => {
    track({
      event_name: TrackingEventName.ToggleUpdated,
      props: {
        toggle_type: 'capture_silence_chime_enabled',
        is_enabled: isEnabled,
        feature: 'dictation',
      },
    });
  },

  trackCleanupModeChanged: (selectedValue) => {
    track({
      event_name: TrackingEventName.ToggleUpdated,
      props: {
        toggle_type: 'dictation_cleanup_mode',
        // Dropdown variant of the discriminated union — read
        // `selected_value`, not `is_enabled`.
        selected_value: selectedValue,
        feature: 'dictation',
      },
    });
  },

  trackHotkeySave: () => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.DictationSettings,
        action: ButtonAction.DictationHotkeySave,
        label: 'Save dictation hotkey',
      },
    });
  },
};
